import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Badge,
  Button,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  AccessTime as PendingIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  TrendingUp,
  AssignmentTurnedIn,
  HourglassEmpty
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";

const APPROVAL_STATUS = {
  pending: { label: 'Pending', color: '#f59e0b', icon: <PendingIcon sx={{ fontSize: 16 }} /> },
  approved: { label: 'Approved', color: '#10b981', icon: <DoneIcon sx={{ fontSize: 16 }} /> },
  rejected: { label: 'Rejected', color: '#ef4444', icon: <CloseIcon sx={{ fontSize: 16 }} /> }
};

const ApproverDashboard = () => {
  const theme = useTheme();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [tabValue, setTabValue] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, approvedToday: 0 });

  useEffect(() => {
    console.log("ApproverDashboard Loaded - Premium Version 1.1");
    if (user?.data?.[0]?.c_id && token) {
      fetchApprovals();
      fetchCounts();
    }
  }, [user, token, page, rowsPerPage, statusFilter]);

  const fetchCounts = async () => {
    if (!user?.data?.[0]?.c_id || !token) return;

    try {
      const statuses = ['pending', 'approved', 'rejected'];
      const countPromises = statuses.map(status =>
        axios.post(
          process.env.REACT_APP_CLIENT_LISTAPPROVALHANDLER,
          {
            env: 'prod',
            approver_id: user.data[0].c_id,
            status: status,
            limit: 1,
            offset: 0
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      );

      const results = await Promise.all(countPromises);
      const newCounts = {
        pending: results[0].data?.data?.count || 0,
        approved: results[1].data?.data?.count || 0,
        rejected: results[2].data?.data?.count || 0,
      };

      // Best effort for "approved today" - since we only have the count from API, 
      // we'd need a specific API for 'today' or filter on client side if we had all data.
      // For now, let's just use the current approved count as a placeholder or keep old logic if valid.
      // But since we are only fetching counts, we'll just set it to the approved count for now 
      // or implement a specific today filter if supported.
      newCounts.approvedToday = results[1].data?.data?.approvals?.filter(a => 
        new Date(a.approved_at).toDateString() === new Date().toDateString()
      ).length || 0;

      setCounts(newCounts);
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  };

  const fetchApprovals = async () => {
    if (!user?.data?.[0]?.c_id || !token) {
      setApprovals([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const offset = page * rowsPerPage;
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_LISTAPPROVALHANDLER,
        {
          env: 'prod',
          approver_id: user.data[0].c_id,
          status: statusFilter || undefined,
          limit: rowsPerPage,
          offset: offset
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data?.data?.approvals) {
        setApprovals(response.data.data.approvals);
        setTotalCount(response.data.data.count || response.data.data.approvals.length);
      } else {
        setApprovals([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(err.response?.data?.data || err.message || 'Failed to load approvals');
      setApprovals([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approval) => {
    setActionLoading(true);
    try {
      await axios.post(
        process.env.REACT_APP_CLIENT_APPROVETASKHANDLER,
        {
          env: 'prod',
          approval_id: approval.id,
          approver_id: user.data[0].c_id,
          agent_id: approval.agent_id,
          action: 'approve'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setApprovals(prev => prev.map(a =>
        a.id === approval.id
          ? { ...a, status: 'approved', approved_at: new Date().toISOString() }
          : a
      ));

      handleCloseDetails();
      fetchCounts();
    } catch (err) {
      setError(err.response?.data?.data || 'Failed to approve task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (approval) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        process.env.REACT_APP_CLIENT_APPROVETASKHANDLER,
        {
          env: 'prod',
          approval_id: approval.id,
          approver_id: user.data[0].c_id,
          agent_id: approval.agent_id,
          action: 'reject',
          reason: rejectReason
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setApprovals(prev => prev.map(a =>
        a.id === approval.id
          ? {
            ...a,
            status: 'rejected',
            rejected_at: new Date().toISOString(),
            rejection_reason: rejectReason
          }
          : a
      ));

      handleCloseDetails();
      fetchCounts();
    } catch (err) {
      setError(err.response?.data?.data || 'Failed to reject task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApproval(approval);
    setDetailsOpen(true);
    setRejectReason('');
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedApproval(null);
    setRejectReason('');
  };

  const filteredApprovals = approvals.filter(approval =>
    approval.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    approval.task_summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getAgentTypeLabel = (type) => {
    const labels = {
      product_owner: 'Product Owner', scrum_master: 'Scrum Master',
      coder: 'Coder', tester: 'Tester', designer: 'Designer',
      devops: 'DevOps', analyst: 'Analyst'
    };
    return labels[type] || type;
  };

  return (
    <PageContainer>
      {/* Header Section */}
      <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="overline" sx={{ color: '#3b82f6', fontWeight: 700, letterSpacing: 1.2 }}>
            AGENT GOVERNANCE
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, letterSpacing: '-0.5px' }}>
            Approvals <span style={{ color: '#3b82f6' }}>Dashboard</span>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
            Govern and validate autonomous agent contributions across your delivery pods.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ActionButton onClick={fetchApprovals} disabled={loading}>
            <RefreshIcon fontSize="small" sx={{ mr: 1, animation: loading ? 'spin 2s linear infinite' : 'none' }} />
            Refresh Feed
          </ActionButton>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <StatCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StatIcon color="#f59e0b"><HourglassEmpty /></StatIcon>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', ml: 2, fontWeight: 600 }}>PENDING REVIEW</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800 }}>{counts.pending}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>Requires immediate oversight</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StatIcon color="#10b981"><AssignmentTurnedIn /></StatIcon>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', ml: 2, fontWeight: 600 }}>APPROVED TODAY</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800 }}>{counts.approvedToday}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>Validated agent throughput</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StatIcon color="#3b82f6"><TrendingUp /></StatIcon>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', ml: 2, fontWeight: 600 }}>SYSTEM VELOCITY</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800 }}>98<span style={{ fontSize: '1.5rem', marginLeft: '4px' }}>%</span></Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>Automated governance efficiency</Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Main Content Area */}
      <GlassPanel>
        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', px: 4, pt: 2 }}>
          <StyledTabs value={tabValue} onChange={(e, v) => {
            setTabValue(v);
            setStatusFilter(['pending', 'approved', 'rejected'][v]);
            setPage(0);
          }}>
            <StyledTab label={<Badge badgeContent={counts.pending} color="error" sx={{ '& .MuiBadge-badge': { top: -2, right: -12 } }}>Pending</Badge>} />
            <StyledTab label={<Badge badgeContent={counts.approved} color="success" sx={{ '& .MuiBadge-badge': { top: -2, right: -12 } }}>Approved</Badge>} />
            <StyledTab label={<Badge badgeContent={counts.rejected} color="warning" sx={{ '& .MuiBadge-badge': { top: -2, right: -12 } }}>Rejected</Badge>} />
          </StyledTabs>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
            <StyledSearch
              fullWidth
              size="small"
              placeholder="Filter by agent name, task ID, or summary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', mr: 1 }} />
              }}
            />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>{error}</Alert>}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>AGENT IDENTITY</StyledHeaderCell>
                  <StyledHeaderCell>TASK CONTEXT</StyledHeaderCell>
                  <StyledHeaderCell>STATUS</StyledHeaderCell>
                  <StyledHeaderCell>TIMESTAMP</StyledHeaderCell>
                  <StyledHeaderCell align="right">GOVERNANCE</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}><CircularProgress size={32} /></TableCell></TableRow>
                ) : filteredApprovals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>No approvals found in this segment</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApprovals.map((approval) => (
                    <StyledBodyRow key={approval.id} hover onClick={() => handleViewDetails(approval)}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontSize: '12px', fontWeight: 700 }}>
                            {approval.agent_name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{approval.agent_name}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{getAgentTypeLabel(approval.agent_type)}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                          {(approval.task_type || 'system_task').replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {approval.task_summary}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={APPROVAL_STATUS[approval.status].label}
                          color={APPROVAL_STATUS[approval.status].color}
                          icon={APPROVAL_STATUS[approval.status].icon}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{formatDate(approval.created_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Detailed Audit">
                          <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#3b82f6' } }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </StyledBodyRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredApprovals.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, v) => setPage(v)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              sx={{ color: 'rgba(255,255,255,0.4)', borderTop: '1px solid rgba(255,255,255,0.05)' }}
            />
          )}
        </Box>
      </GlassPanel>

      {/* Audit Detail Dialog */}
      <StyledDialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Audit Trail</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Task ID: {selectedApproval?.id}</Typography>
          </Box>
          {selectedApproval && <StatusChip label={APPROVAL_STATUS[selectedApproval.status].label} color={APPROVAL_STATUS[selectedApproval.status].color} icon={APPROVAL_STATUS[selectedApproval.status].icon} />}
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {selectedApproval && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700 }}>CONTRIBUTOR</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6' }}>{selectedApproval.agent_name}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{getAgentTypeLabel(selectedApproval.agent_type)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700 }}>TASK OBJECTIVE</Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>{selectedApproval.task_summary}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailBox>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>TRIGGERED BY</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedApproval.triggered_by}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block' }}>CREATION TIME</Typography>
                    <Typography variant="body2">{formatDate(selectedApproval.created_at)}</Typography>
                  </Box>
                </DetailBox>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, mb: 1, display: 'block' }}>TECHNICAL MANIFEST</Typography>
                <ManifestBox>
                  <pre>{JSON.stringify(selectedApproval.task_details, null, 2)}</pre>
                </ManifestBox>
              </Grid>

              {selectedApproval.status === 'pending' && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, mb: 1, display: 'block' }}>GOVERNANCE COMMENTS</Typography>
                  <StyledInput
                    fullWidth
                    placeholder="Provide context if rejecting this contribution..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 4, gap: 2 }}>
          <Button onClick={handleCloseDetails} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}>Cancel Audit</Button>
          {selectedApproval?.status === 'pending' && (
            <>
              <RejectButton
                onClick={() => handleReject(selectedApproval)}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <RejectIcon />}
              >
                Restrict Deployment
              </RejectButton>
              <ApproveButton
                onClick={() => handleApprove(selectedApproval)}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
              >
                Approve for Production
              </ApproveButton>
            </>
          )}
        </DialogActions>
      </StyledDialog>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled(Box)`
  padding: 40px;
  background: transparent;
`;

const StatCard = styled(Box)`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 32px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(59, 130, 246, 0.3);
  }
`;

const StatIcon = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => alpha(props.color, 0.1)};
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionButton = styled(Button)`
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.2);
  padding: 8px 20px;
  border-radius: 10px;
  text-transform: none;
  font-weight: 600;

  &:hover {
    background: rgba(59, 130, 246, 0.15);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const GlassPanel = styled(Box)`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
`;

const StyledTabs = styled(Tabs)`
  & .MuiTabs-indicator {
    background-color: #3b82f6;
    height: 3px;
    border-radius: 3px;
  }
`;

const StyledTab = styled(Tab)`
  text-transform: none;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  padding: 20px 24px;
  &.Mui-selected {
    color: #fff;
  }
`;

const StyledSearch = styled(TextField)`
  & .MuiOutlinedInput-root {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    color: #fff;
    & fieldset {
      border-color: rgba(255, 255, 255, 0.05);
    }
    &:hover fieldset {
      border-color: rgba(255, 255, 255, 0.1);
    }
    &.Mui-focused fieldset {
      border-color: #3b82f6;
    }
  }
`;

const StyledHeaderCell = styled(TableCell)`
  color: rgba(255, 255, 255, 0.3) !important;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 16px;
`;

const StyledBodyRow = styled(TableRow)`
  cursor: pointer;
  & td {
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    padding: 20px 16px;
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.02) !important;
  }
`;

const StatusChip = styled(({ label, color, icon, ...props }) => (
  <Box {...props} sx={{
    display: 'inline-flex', alignItems: 'center', gap: 1,
    px: 1.5, py: 0.5, borderRadius: '8px',
    bgcolor: alpha(color, 0.1), color: color,
    border: `1px solid ${alpha(color, 0.2)}`,
    fontSize: '11px', fontWeight: 700
  }}>
    {icon} {label}
  </Box>
))``;

const StyledDialog = styled(Dialog)`
  & .MuiPaper-root {
    background: #111827;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    color: #fff;
  }
`;

const DetailBox = styled(Box)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
`;

const ManifestBox = styled(Box)`
  background: #000;
  border-radius: 12px;
  padding: 20px;
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #10b981;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(16, 185, 129, 0.1);
  & pre {
    margin: 0;
    white-space: pre-wrap;
  }
`;

const StyledInput = styled(TextField)`
  & .MuiOutlinedInput-root {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    color: #fff;
    & fieldset { border-color: rgba(255, 255, 255, 0.05); }
    &:hover fieldset { border-color: rgba(255, 255, 255, 0.1); }
    &.Mui-focused fieldset { border-color: #ef4444; }
  }
`;

const ApproveButton = styled(Button)`
  background: #10b981;
  color: #fff;
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
  padding: 10px 24px;
  &:hover { background: #059669; }
`;

const RejectButton = styled(Button)`
  background: transparent;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
  padding: 10px 24px;
  &:hover { background: rgba(239, 68, 68, 0.05); border-color: #ef4444; }
`;

export default ApproverDashboard;